import type { FC, ReactElement, FormEvent } from 'react';
import { useState, useEffect, useCallback } from 'react';
import './CheckIn.css';
import LocationMap from './components/LocationMap';
import LocationForm from './components/LocationForm';
import CheckInHistory from './components/CheckInHistory';
import PermissionRequest from './components/PermissionRequest';
import Modal from './components/Modal';
import { loadGoogleMapsScript } from './utils/maps';
import { Coordinates, CheckHistoryEntry } from './types';
import { LocationState, SavedLocation } from './types/locationState';
import { supabase } from '../../lib/supabaseClient';
import { useStudentAuth } from '../../lib/AuthProvider';


/// <reference types="@types/google.maps" />

// Declare google maps to avoid TS errors
declare global {
  interface Window {
    google: {
      maps: typeof google.maps;
    };
    initMap?: () => void;
  }
}

interface CheckInProps {
  onClose: () => void;
}

const CheckIn: FC<CheckInProps> = ({ onClose }): ReactElement | null => {
  // Get loading state from AuthProvider
  const { loading: authLoading } = useStudentAuth();
  
  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="checkin-modal-overlay">
        <div className="checkin-modal">
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div>Loading your profile...</div>
            <div style={{ fontSize: '0.8rem', marginTop: '10px', color: '#666' }}>
              Please wait a moment...
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Constants
  const MIN_ACCURACY = 50; // meters - only accept locations with accuracy better than this
  const SIGNIFICANT_CHANGE = 5; // meters - minimum distance to consider as actual movement

  // Consolidated location state
  const [locationState, setLocationState] = useState<LocationState>({
    current: null,
    lastAccurate: null,
    selected: null,
    saved: null,
    savedLocations: []
  });
  const [isWithinRadius, setIsWithinRadius] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [showAddLocation, setShowAddLocation] = useState(false);

  // UI state
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [radius, setRadius] = useState(100); // Default radius in meters
  const [status, setStatus] = useState('Not in designated area');
  const [lastAction, setLastAction] = useState('None');

  // Check-in state
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [checkInHistory, setCheckInHistory] = useState<CheckHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);



  // Load check-in history from database using typed supabase client
  useEffect(() => {
    const loadCheckInHistory = async () => {
      setIsLoadingHistory(true);
      setHistoryError(null);
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (!authUser) {
          setHistoryError('Please log in to view check-in history');
          return;
        }

        // Simple query without joins to avoid foreign key issues
        
        // First try a simple query without joins to isolate the issue
        const { data: checkIns, error } = await supabase
          .from('check_ins')
          .select('check_type, check_time, coordinates, location_id')
          .eq('user_id', authUser.id)
          .order('check_time', { ascending: false })
          .limit(50) as { data: any[] | null, error: any };

        if (error) {
          return;
        }

        if (checkIns) {
          const history: CheckHistoryEntry[] = checkIns.map(checkIn => ({
            type: checkIn.check_type.includes('in') ? 'in' : 'out',
            time: new Date(checkIn.check_time),
            location: 'Location ID: ' + checkIn.location_id, // Temporary - we'll get location name separately
            coordinates: checkIn.coordinates as unknown as Coordinates
          }));
          setCheckInHistory(history);
        }
      } catch (error) {
        setHistoryError('Failed to load check-in history');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadCheckInHistory();
  }, []); // Load history on component mount

  useEffect(() => {
    // Request location permission when component mounts
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(status => {
          setPermissionStatus(status.state as 'prompt' | 'granted' | 'denied');
          if (status.state === 'granted') {
            getCurrentLocation();
            // Start continuous location tracking
            const watchId = navigator.geolocation.watchPosition(
              (position) => {
                const newLocation = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                };
                setLocationState(prev => ({ ...prev, current: newLocation }));
              },
              () => {
                setStatus('Error updating location');
              },
              {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 2000
              }
            );

            // Cleanup on unmount
            return () => {
              navigator.geolocation.clearWatch(watchId);
            };
          }
        });
    }
  }, []);

  const getCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocationState(prev => ({ ...prev, current: newLocation }));
        setStatus('Location acquired successfully');
      },
      (error) => {
        switch (error.code) {
          case GeolocationPositionError.PERMISSION_DENIED:
            setStatus('Location access was denied');
            setPermissionStatus('denied');
            break;
          case GeolocationPositionError.POSITION_UNAVAILABLE:
            setStatus('Unable to determine your location');
            break;
          case GeolocationPositionError.TIMEOUT:
            setStatus('Location request timed out');
            break;
          default:
            setStatus('Error getting location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };



  // Map loading state
  useEffect(() => {
    if (!locationState.current || !window.google?.maps) {
      setIsMapLoading(true);
    } else {
      setIsMapLoading(false);
    }
  }, [locationState.current]);

  // Helper function to check and update distance
  const checkAndUpdateDistance = (coords: Coordinates, accuracy: number) => {
    if (locationState.saved) {
      const distance = calculateDistance(coords, {
        lat: locationState.saved.lat,
        lng: locationState.saved.lng
      });
      const isInRange = distance <= locationState.saved.radius;
      setIsWithinRadius(isInRange);
      setStatus(isInRange ? 
        `Within designated area (${Math.round(distance)}m from location, Accuracy: ${accuracy.toFixed(1)}m)` : 
        `Outside designated area (${Math.round(distance)}m from location, Accuracy: ${accuracy.toFixed(1)}m)`
      );
    }
  };


  // Load saved locations on component mount
  useEffect(() => {
    const loadSavedLocations = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: locations, error } = await supabase
          .from('locations')
          .select()
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }) as { data: any[] | null, error: any };

        if (error) {
          return;
        }

        if (locations) {
          const loadedLocations: SavedLocation[] = locations.map(location => ({
            id: location.id,
            name: location.name,
            radius: location.radius,
            lat: location.latitude,
            lng: location.longitude,
            is_active: location.is_active
          }));
          setLocationState(prev => ({ ...prev, savedLocations: loadedLocations }));
          // Set the active location if there is one
          const activeLocation = loadedLocations.find(loc => loc.is_active);
          if (activeLocation) {
            setLocationState(prev => ({ 
              ...prev, 
              saved: activeLocation,
              selected: { lat: activeLocation.lat, lng: activeLocation.lng }
            }));
            setLocationName(activeLocation.name);
            setRadius(activeLocation.radius);
          } else if (loadedLocations.length > 0) {
            // Set the first location as active if none are marked as active
            const firstLocation = loadedLocations[0];
            setLocationState(prev => ({ 
              ...prev, 
              saved: firstLocation,
              selected: { lat: firstLocation.lat, lng: firstLocation.lng }
            }));
            setLocationName(firstLocation.name);
            setRadius(firstLocation.radius);
          }
        } else {
          setLocationState(prev => ({ ...prev, savedLocations: [] }));
        }
      } catch (error) {
        setLocationState(prev => ({ ...prev, savedLocations: [] }));
      }
    };

    loadSavedLocations();
  }, []); // Load locations on component mount

  const calculateDistance = useCallback((coords1: Coordinates, coords2: Coordinates): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (coords1.lat * Math.PI) / 180;
    const φ2 = (coords2.lat * Math.PI) / 180;
    const Δφ = ((coords2.lat - coords1.lat) * Math.PI) / 180;
    const Δλ = ((coords2.lng - coords1.lng) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const checkDistance = useCallback((currentCoords: Coordinates) => {
    if (locationState.saved) {
      const distance = calculateDistance(currentCoords, {
        lat: locationState.saved.lat,
        lng: locationState.saved.lng
      });
      const isInRange = distance <= Number(locationState.saved.radius);
      setIsWithinRadius(isInRange);
      setStatus(isInRange ? 
        `Within designated area (${Math.round(distance)}m from location)` : 
        `Outside designated area (${Math.round(distance)}m from location)`
      );
    }
  }, [locationState.saved, calculateDistance]);

  const startLocationTracking = useCallback((): (() => void) => {
    let watchId: number;
    let retryTimeout: NodeJS.Timeout;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const setupWatcher = () => {
      try {
        watchId = navigator.geolocation.watchPosition(
          (position) => {
            // Reset retry count on successful update
            retryCount = 0;
            
            // Only update if accuracy is good enough
            if (position.coords.accuracy <= MIN_ACCURACY) {
              const newCoords: Coordinates = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };

              // Check if this is a significant change from last accurate location
              if (!locationState.lastAccurate || calculateDistance(locationState.lastAccurate, newCoords) >= SIGNIFICANT_CHANGE) {
                setLocationState(prev => ({ 
                  ...prev, 
                  current: newCoords,
                  lastAccurate: newCoords
                }));
                checkAndUpdateDistance(newCoords, position.coords.accuracy);
              }
            } else {
              setStatus(`Waiting for better location accuracy (Current: ${position.coords.accuracy.toFixed(1)}m)`);
            }
            setPermissionStatus('granted');
          },
          (error: GeolocationPositionError) => {
            if (error.code === GeolocationPositionError.TIMEOUT) {
              if (retryCount < MAX_RETRIES) {
                retryCount++;
                setStatus(`Location update timed out. Retrying... (Attempt ${retryCount}/${MAX_RETRIES})`);
                
                // Clear existing watcher and retry after a short delay
                if (watchId) {
                  navigator.geolocation.clearWatch(watchId);
                }
                retryTimeout = setTimeout(setupWatcher, 2000);
                return;
              }
            }

            switch (error.code) {
              case GeolocationPositionError.PERMISSION_DENIED:
                setStatus('Location access was denied');
                setPermissionStatus('denied');
                break;
              case GeolocationPositionError.POSITION_UNAVAILABLE:
                setStatus('Unable to determine your location. Please check your device settings.');
                getCurrentLocation(); // Try to get a single position update
                break;
              case GeolocationPositionError.TIMEOUT:
                if (retryCount >= MAX_RETRIES) {
                  setStatus('Location tracking failed. Please refresh the page or check your device settings.');
                }
                break;
              default:
                setStatus('Error getting location. Please try again.');
            }
          },
          {
            enableHighAccuracy: true,
            maximumAge: 2000,     // Allow cached positions up to 2 seconds old
            timeout: 10000        // Increased timeout to 10 seconds
          }
        );
      } catch (error) {
      }
    };

    try {
      // Start the initial watcher
      setupWatcher();

      // Return cleanup function
      return () => {
        if (watchId) {
          navigator.geolocation.clearWatch(watchId);
        }
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
      };
    } catch (error) {
      setStatus('Error setting up location tracking');
      return () => {}; // Return empty cleanup function on error
    }
}, [locationState.saved, calculateDistance, getCurrentLocation]);

  const requestLocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPermissionStatus('granted');
        setShowModal(true);
        const coords: Coordinates = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocationState(prev => ({ ...prev, current: coords }));
        startLocationTracking();
        
        // Check if location is already saved
        if (locationState.saved) {
          const distance = calculateDistance(coords, {
            lat: locationState.saved.lat,
            lng: locationState.saved.lng
          });
          const isInRange = distance <= locationState.saved.radius;
          setIsWithinRadius(isInRange);
          setStatus(isInRange ? 
            `Within designated area (${Math.round(distance)}m from location)` : 
            `Outside designated area (${Math.round(distance)}m from location)`
          );
        }
      },
      (error) => {
        // Check if it's a timeout error
        if (error.code === error.TIMEOUT) {
          setStatus('Location request timed out. Please try again.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setStatus('Unable to determine your location. Please check your device settings.');
        } else {
          setPermissionStatus('denied');
          setStatus('Location permission denied');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [locationState.saved, startLocationTracking, calculateDistance]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let permResult: PermissionStatus;
    
    const handlePermissionChange = () => {
      if (!permResult) return;
      setPermissionStatus(permResult.state as 'prompt' | 'granted' | 'denied');
      if (permResult.state === 'granted') {
        cleanup = startLocationTracking();
        setShowModal(true);
      }
    };

    const checkAndRequestLocation = async () => {
      if (!navigator.geolocation) {
        setStatus('Geolocation is not supported by your browser');
        return;
      }

      try {
        // First check if geolocation permission is already granted
        permResult = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(permResult.state as 'prompt' | 'granted' | 'denied');

        if (permResult.state === 'granted') {
          // If already granted, start tracking immediately
          cleanup = startLocationTracking();
          setShowModal(true);
          
          // Listen for permission changes
          permResult.addEventListener('change', handlePermissionChange);
        } else if (permResult.state === 'prompt') {
          // If not granted, request permission
          requestLocationPermission();
        }
      } catch (error) {
        // Fall back to direct geolocation request
        requestLocationPermission();
      }
    };

    checkAndRequestLocation();

    // Cleanup function
    return () => {
      if (cleanup) {
        cleanup();
      }
      if (permResult) {
        permResult.removeEventListener('change', handlePermissionChange);
      }
    };
  }, [startLocationTracking, requestLocationPermission, setStatus]);

  // Load Google Maps script as soon as possible
  useEffect(() => {
    loadGoogleMapsScript(() => setIsMapLoading(false));
  }, []); // Load script immediately when component mounts

  // Monitor location updates
  useEffect(() => {
    if (locationState.current && permissionStatus === 'granted') {
      setIsMapLoading(false);
    }
  }, [locationState.current, permissionStatus]);

  // Check distance when saved location changes
  useEffect(() => {
    if (locationState.current && locationState.saved) {
      checkDistance(locationState.current);
    }
  }, [locationState.saved, checkDistance]);







  const handleTimeAction = async (action: 'in' | 'out'): Promise<void> => {
    if (!isWithinRadius) {
      alert('You must be within the designated area to time ' + action);
      return;
    }
    
    if (!locationState.current || !locationState.saved) {
      alert('Current location or saved work location not available');
      return;
    }

    const now = new Date();
    const historyEntry = {
      type: action,
      time: now,
      location: locationState.saved.name,
      coordinates: locationState.current
    };

    if (action === 'in') {
      if (isCheckedIn) {
        alert('You are already checked in');
        return;
      }
      setIsCheckedIn(true);
      setCheckInTime(now);
      setLastAction(`Checked in at ${now.toLocaleTimeString()}`);
      setStatus(`Checked in at ${locationState.saved.name}`);
    } else {
      if (!isCheckedIn) {
        alert('You must check in before checking out');
        return;
      }
      
      setIsCheckedIn(false);
      const duration = checkInTime ? Math.round((now.getTime() - checkInTime.getTime()) / (1000 * 60)) : 0;
      setLastAction(`Checked out at ${now.toLocaleTimeString()} (Duration: ${duration} minutes)`);
      setCheckInTime(null);
      setStatus('Checked out');
    }

    // Add to history
    setCheckInHistory(prev => [...prev, historyEntry]);

    // Save check-in/out to the database using Database types
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('User not authenticated');
      }

      // Define the expected profile type
      type ProfileData = {
        first_name: string;
        last_name: string;
        student_id: string;
      };

      // Get user profile data with proper typing
      const { data: profile, error: profileError } = await supabase
        .from('student_profile')
        .select('first_name, last_name, student_id')
        .eq('id', authUser.id)
        .single() as { data: ProfileData | null, error: any };

      if (profileError) {
        throw new Error(`Profile query failed: ${profileError.message}`);
      }
      
      if (!profile) {
        throw new Error('Profile not found');
      }

      // Define the expected location type
      type LocationData = {
        id: string;
      };

      // Get location ID
      const { data: locationData, error: locationError } = await supabase
        .from('locations')
        .select('id')
        .eq('user_id', authUser.id)
        .eq('is_active', true)
        .single() as { data: LocationData | null, error: any };

      if (locationError || !locationData) {
        throw new Error(locationError?.message || 'Active location not found');
      }

      // Determine check type based on time and action
      const hour = now.getHours();
      const checkType = action === 'in' 
        ? (hour < 12 ? 'check_in_am' : 'check_in_pm')
        : (hour < 12 ? 'check_out_am' : 'check_out_pm');

      // Create a properly typed object for inserting into check_ins table
      const checkInData = {
        user_id: authUser.id,
        student_id: profile.student_id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        location_id: locationData.id,
        check_type: checkType as 'check_in_am' | 'check_in_pm' | 'check_out_am' | 'check_out_pm',
        check_time: now.toISOString(),
        check_date: now.toISOString().split('T')[0],
        is_within_radius: isWithinRadius,
        coordinates: locationState.current as unknown as Record<string, unknown>
      };

      // Type for supabase insert operation
      type SupabaseInsertOperation = {
        from: (table: string) => {
          insert: (data: Record<string, any>) => Promise<{error: any}>;
        };
      };

      // Using proper typing for the insert operation
      const supabaseTyped = supabase as unknown as SupabaseInsertOperation;
      const { error: insertError } = await supabaseTyped
        .from('check_ins')
        .insert(checkInData);

      if (insertError) {
        throw insertError;
      }

    } catch (error) {
      alert('Error saving check-in/out. Please try again.');
    }
  };

  const handleLocationSelect = (location: Coordinates): void => {
    setLocationState(prev => ({ ...prev, selected: location }));
  };

  const handleSelectActiveLocation = async (locationId: string): Promise<void> => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('You must be logged in to select a location');
        return;
      }

      // Type for supabase operation
      type SupabaseUpdateOperation = {
        from: (table: string) => {
          update: (updatePayload: Record<string, any>) => {
            eq: (column: string, value: any) => Promise<any>;
          };
        };
      };

      // Deactivate all locations - using type casting for the entire operation
      const supabaseTyped = supabase as unknown as SupabaseUpdateOperation;
      
      await supabaseTyped
        .from('locations')
        .update({ is_active: false })
        .eq('user_id', authUser.id);

      // Activate the selected location
      await supabaseTyped
        .from('locations')
        .update({ is_active: true })
        .eq('id', locationId);

      // Update local state and form fields in one atomic operation
      setLocationState(prev => {
        const updatedLocations = prev.savedLocations.map(loc => ({
          ...loc,
          is_active: loc.id === locationId
        }));
        
        const selectedLoc = updatedLocations.find(loc => loc.id === locationId);
        
        if (selectedLoc) {
          // Update form fields immediately with the selected location data
          setLocationName(selectedLoc.name);
          setRadius(selectedLoc.radius);
          
          // Check distance if current location is available - use the new location data directly
          if (prev.current) {
            // Calculate distance directly without relying on stale state
            const distance = calculateDistance(prev.current, {
              lat: selectedLoc.lat,
              lng: selectedLoc.lng
            });
            const isInRange = distance <= Number(selectedLoc.radius);
            
            setIsWithinRadius(isInRange);
            setStatus(isInRange ? 
              `Within designated area (${Math.round(distance)}m from ${selectedLoc.name})` : 
              `Outside designated area (${Math.round(distance)}m from ${selectedLoc.name})`
            );
          }
          
          return {
            ...prev,
            savedLocations: updatedLocations,
            saved: selectedLoc,
            selected: { lat: selectedLoc.lat, lng: selectedLoc.lng }
          };
        }
        
        return {
          ...prev,
          savedLocations: updatedLocations
        };
      });
    } catch (error) {
      alert('Error selecting location');
    }
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!locationState.selected) {
      alert('Please select a work location on the map');
      return;
    }

    if (!locationName.trim()) {
      alert('Please enter a location name');
      return;
    }

    if (radius < 50 || radius > 500) {
      alert('Please enter a radius between 50 and 500 meters');
      return;
    }

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        alert('You must be logged in to save a location');
        return;
      }

      // Create a properly typed location object using the Database types
      const locationData = {
        user_id: authUser.id,
        name: locationName.trim(),
        latitude: locationState.selected.lat,
        longitude: locationState.selected.lng,
        radius: radius,
        is_active: false // New locations are not active by default
      };

      // Insert new location using direct Supabase query
      
      // Try a simple insert first without select to avoid foreign key issues
      const { error: insertError } = await (supabase as any)
        .from('locations')
        .insert(locationData);
        
      if (insertError) {
        throw insertError;
      }
      
      // If insert succeeded, get the inserted data
      const { data: insertedLocation, error: selectError } = await supabase
        .from('locations')
        .select('*')
        .eq('user_id', authUser.id)
        .eq('name', locationName.trim())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (selectError) {
        throw selectError;
      }
      
      const newLocationData = insertedLocation;

      if (!newLocationData) {
        alert('Error saving location: No data returned');
        return;
      }

      // Cast the returned data to our SavedLocation interface
      const newLocation = newLocationData as unknown as {
        id: string;
        name: string;
        radius: number;
        latitude: number;
        longitude: number;
        is_active: boolean;
      };

      // Add the new location to the list
      const savedLocation: SavedLocation = {
        id: newLocation.id,
        name: newLocation.name,
        radius: newLocation.radius,
        lat: newLocation.latitude,
        lng: newLocation.longitude,
        is_active: newLocation.is_active
      };
      
      setLocationState(prev => ({
        ...prev,
        savedLocations: [...prev.savedLocations, savedLocation],
        saved: savedLocation
      }));
      
      // Ensure location tracking is active after saving
      if (permissionStatus === 'granted') {
        if (locationState.current) {
          checkDistance(locationState.current);
        }
        startLocationTracking();
        setStatus(`Location "${locationName}" saved with ${radius}m radius`);
      } else {
        // Re-request location permission if needed
        requestLocationPermission();
      }
    } catch (error: any) {
      if (error?.code === '42P01' && error?.message?.includes('profiles')) {
        alert('Database schema error: The locations table references a profiles table that doesn\'t exist. The table has been renamed to student_profile. Please contact your administrator.');
      } else {
        alert(`Error saving location: ${error?.message || 'Unknown error'}`);
      }
    }
  };

  // requestLocationPermission is now defined above

  // Show permission request modal if permission is not granted
  if (!showModal) {
    return (
      <PermissionRequest
        permissionStatus={permissionStatus}
        status={status}
        onRequestPermission={requestLocationPermission}
        onClose={onClose}
      />
    );
  }


  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      title="Time In / Time Out"
    >
      <LocationMap
        currentLocation={locationState.current}
        selectedLocation={locationState.selected}
        radius={radius}
        isLoading={isMapLoading}
        onLocationSelect={handleLocationSelect}
        onMarkerDrag={(location) => setLocationState(prev => ({ ...prev, selected: location }))}
      />

      <LocationForm
        locationName={locationName}
        radius={radius}
        isCheckedIn={isCheckedIn}
        isWithinRadius={isWithinRadius}
        checkInTime={checkInTime}
        lastAction={lastAction}
        onLocationNameChange={setLocationName}
        onRadiusChange={setRadius}
        onTimeAction={handleTimeAction}
        onSubmit={handleSubmit}
        savedLocation={locationState.saved}
        savedLocations={locationState.savedLocations}
        showAddLocation={showAddLocation}
        onToggleAddLocation={() => setShowAddLocation(!showAddLocation)}
        onSelectLocation={handleSelectActiveLocation}
      />

      <CheckInHistory 
        history={checkInHistory}
        isLoading={isLoadingHistory}
        error={historyError}
      />
    </Modal>
  );
};



export default CheckIn;